import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import withAuth from '../utils/withAuth';
import fallbackAvatar from "../assets/avatar-6.png";
import { fetchAllDataAPI } from '../utils/api';
import { BASEAGENTURLS } from '../utils/BaseUrl';

/* ---------------- spotlight settings (yahin se badal sakte ho) ---------------- */
const SPOT_INTERVAL = 10000; // har kitne ms me spotlight aaye (10 second)
const SPOT_SHOW_FOR = 6500;  // kitni der screen par rahe
const SPOT_LABELS = ['Champion', 'Runner up', 'Third place', 'Fourth place', 'Fifth place'];
const SPOT_CONFETTI = ['🎉', '✨', '⭐', '🎊', '✨', '🎈'];

/* ---------------- helpers ---------------- */
const getYoutubeId = (url) => {
    const m = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
    return m ? m[1] : '';
};

let youtubeApiPromise;
const loadYoutubeApi = () => {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (youtubeApiPromise) return youtubeApiPromise;
    youtubeApiPromise = new Promise(resolve => {
        const previous = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof previous === 'function') previous();
            resolve(window.YT);
        };
        if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
            const script = document.createElement('script');
            script.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(script);
        }
    });
    return youtubeApiPromise;
};

const boardMediaUrl = value => !value || /^https?:\/\//i.test(value)
    ? value
    : `https://satkartar.one/assets/tvboard/${value}`;

const sortTop = (arr) => [...arr].sort((a, b) => b.total_order_amt - a.total_order_amt).slice(0, 5);

const svgURI = (s) => 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(s);

const scene = (k) => {
    const bg = ['#16305E', '#4A1F2A', '#1F4A3F'][k];
    const ac = ['#D4A74A', '#F0B429', '#7FD1B0'][k];
    let b = '';
    if (k === 0) {
        for (let i = 0; i < 4; i++) for (let j = 0; j <= i; j++)
            b += `<rect x="${40 + j * 44 + (3 - i) * 22}" y="${130 - i * 34}" width="40" height="30" fill="${ac}" opacity="${0.95 - i * 0.12}"/>`;
    }
    if (k === 1) b = `<path d="M100 150L160 30L220 150z" fill="${ac}"/><rect x="154" y="64" width="12" height="46" fill="#4A1F2A"/><rect x="154" y="120" width="12" height="12" fill="#4A1F2A"/>`;
    if (k === 2) b = `<rect x="40" y="70" width="140" height="64" fill="${ac}"/><rect x="180" y="92" width="64" height="42" fill="${ac}" opacity=".8"/><circle cx="80" cy="140" r="14" fill="#0B1220" stroke="${ac}" stroke-width="5"/><circle cx="210" cy="140" r="14" fill="#0B1220" stroke="${ac}" stroke-width="5"/>`;
    return svgURI(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 160"><rect width="260" height="160" fill="${bg}"/>${b}</svg>`);
};

const POSTER = svgURI('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 90"><rect width="160" height="90" fill="#16305E"/><circle cx="80" cy="45" r="16" fill="none" stroke="#D4A74A" stroke-width="2"/><path d="M75 36v18l15-9z" fill="#D4A74A"/></svg>');

const CountUp = ({ value, prefix = '' }) => {
    const [shown, setShown] = useState(0);
    const from = useRef(0);
    useEffect(() => {
        const start = performance.now();
        const a = from.current;
        let raf;
        const step = (t) => {
            const p = Math.min((t - start) / 1200, 1);
            const v = Math.round(a + (value - a) * (1 - Math.pow(1 - p, 3)));
            from.current = v;
            setShown(v);
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [value]);
    return <>{prefix}{shown.toLocaleString('en-IN')}</>;
};

// spotlight ke liye: 0 se value tak, delay ke baad shuru hota hai
const SpotCount = ({ value, prefix = '', delay = 0 }) => {
    const [shown, setShown] = useState(0);
    useEffect(() => {
        let raf;
        const t0 = performance.now() + delay;
        const step = (t) => {
            const p = Math.min(Math.max((t - t0) / 1600, 0), 1);
            setShown(Math.round(value * (1 - Math.pow(1 - p, 3))));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [value, delay]);
    return <>{prefix}{shown.toLocaleString('en-IN')}</>;
};

/* ---------------- Leader spotlight: har 10 second me ek performer, Top 5 section ke andar ---------------- */
const LeaderSpotlight = ({ list, imageOf }) => {
    const [spot, setSpot] = useState(null);
    const listRef = useRef(list);
    const counter = useRef(0);

    useEffect(() => { listRef.current = list; }, [list]);

    // 1, 2, 3, 4, 5 aur phir dobara 1...
    useEffect(() => {
        let hideTimer;
        const tick = () => {
            const l = listRef.current;
            if (!l.length) return;
            const i = counter.current % l.length;
            counter.current += 1;
            setSpot({ i, id: counter.current });
            clearTimeout(hideTimer);
            hideTimer = setTimeout(() => setSpot(null), SPOT_SHOW_FOR);
        };
        const t = setInterval(tick, SPOT_INTERVAL);
        return () => { clearInterval(t); clearTimeout(hideTimer); };
    }, []);

    const p = spot ? list[spot.i] : null;
    const total = list.reduce((s, x) => s + x.total_order_amt, 0) || 1;
    const pct = p ? Math.round((p.total_order_amt / total) * 100) : 0;
    const champ = !!spot && spot.i === 0;

    return (
        <AnimatePresence>
            {spot && p && (
                <motion.div
                    key={spot.id}
                    className={`wlbs ${champ ? 'champ' : ''}`}
                    initial={{ x: '-100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
                >
                    <div className="wlbs-rankbg">{spot.i + 1}</div>

                    <motion.div
                        className="wlbs-avatar"
                        initial={{ scale: 0, rotate: -25 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.5, type: 'spring', stiffness: 120, damping: 12 }}
                    >
                        <img src={imageOf(p.image)} alt={p.name} />
                        {champ && <span className="wlbs-crown">👑</span>}
                    </motion.div>

                    <div className="wlbs-info">
                        <motion.div className="wlbs-label" initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.7 }}>
                            {SPOT_LABELS[spot.i] || `#${spot.i + 1}`}
                        </motion.div>
                        <motion.div className="wlbs-name" initial={{ x: 120, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.85, type: 'spring', stiffness: 90, damping: 16 }}>
                            {p.name}
                        </motion.div>
                        <motion.div className="wlbs-emp" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
                            {p.empId}
                        </motion.div>
                        <motion.div className="wlbs-amt" initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 1.1, type: 'spring', stiffness: 100, damping: 12 }}>
                            <SpotCount prefix="₹" value={p.total_order_amt} delay={1100} />
                        </motion.div>
                        <motion.div className="wlbs-ord" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}>
                            {p.total_order} {p.total_order === 1 ? 'order' : 'orders'} · Top 5 ki sales ka {pct}%
                        </motion.div>
                    </div>

                    <div className="wlbs-votebar">
                        <motion.i initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ delay: 1.2, duration: 1.8, ease: 'easeOut' }} />
                    </div>

                    {champ && Array.from({ length: 18 }).map((_, k) => (
                        <span
                            key={k}
                            className="wlbs-c"
                            style={{ left: `${(k * 53 + 7) % 96}%`, animationDelay: `${(k % 6) * 0.45}s` }}
                        >{SPOT_CONFETTI[k % SPOT_CONFETTI.length]}</span>
                    ))}
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/* ---------------- CSS (isi file me) ---------------- */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@500;600&family=Hind:wght@400;500;600&display=swap');
.wlb-root{--navy:#0B1220;--panel:#121B2E;--line:#243252;--gold:#D4A74A;--ivory:#F3EEE2;--mute:#D2DAEC;--red:#C8102E;
  --head:'Oswald','Arial Narrow',sans-serif;--body:'Hind','Segoe UI',sans-serif;
  position:fixed;inset:0;background:var(--navy);overflow:hidden;color:var(--ivory);font-family:var(--body);z-index:9999}
.wlb-root *{box-sizing:border-box;margin:0}
.wlb-stage{width:1920px;height:1080px;position:absolute;transform-origin:0 0;display:grid;grid-template-rows:96px 1fr 84px;
  background:radial-gradient(1200px 700px at 80% -10%,#16264A 0%,var(--navy) 60%)}
.wlb-stage.no-ticker{grid-template-rows:96px 1fr}
.wlb-top{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;padding:0 40px;border-bottom:2px solid var(--gold)}
.wlb-logo{display:flex;align-items:center;gap:18px;font:600 38px var(--head);letter-spacing:.04em}
.wlb-logo i{width:52px;height:52px;border:2px solid var(--gold);display:grid;place-items:center;font:600 24px var(--head);color:var(--gold);font-style:normal}
.wlb-identity{display:flex;flex-direction:column;align-items:flex-start;border:0;background:transparent;color:var(--ivory);padding:0;line-height:1;cursor:pointer;text-align:left}.wlb-identity strong{font:600 38px var(--head);letter-spacing:.04em}.wlb-identity small{font:500 17px var(--body);color:var(--gold);margin-top:7px;letter-spacing:0}.wlb-identity:hover small{text-decoration:underline;color:#f6cf7a}
.wlb-live{display:flex;align-items:center;gap:10px;background:var(--red);padding:6px 18px;font:500 24px var(--head);letter-spacing:.08em}
.wlb-live i{width:12px;height:12px;border-radius:50%;background:#fff;animation:wlbpulse 1.2s infinite}
@keyframes wlbpulse{50%{opacity:.2}}
.wlb-time{justify-self:end;text-align:right;font:500 40px var(--head);line-height:1}
.wlb-date{display:block;font:500 26px var(--body);color:var(--mute);margin-top:4px}
.wlb-main{display:grid;grid-template-columns:1100px 1fr;gap:32px;padding:28px 40px;min-height:0}
.wlb-main.no-performers{grid-template-columns:minmax(0,1fr)}
.wlb-col{display:flex;flex-direction:column;gap:24px;min-height:0}
.wlb-video{position:relative;width:1100px;height:619px;background:#000;border:1px solid var(--line);overflow:hidden}
.wlb-main.no-performers .wlb-video{width:100%}.wlb-col.no-slides .wlb-video{height:100%}
.wlb-video img,.wlb-video iframe,.wlb-youtube-player,.wlb-youtube-player>div{position:absolute;inset:0;width:100%;height:100%;border:0}.wlb-banner{object-fit:contain;background:#000}
.wlb-tag{position:absolute;left:0;bottom:0;background:var(--gold);color:var(--navy);padding:8px 24px;font:600 26px var(--head);z-index:2}
.wlb-slides{position:relative;flex:1;background:var(--panel);border:1px solid var(--line);overflow:hidden}
.wlb-slide{position:absolute;inset:0;display:flex;align-items:center;gap:28px;padding:20px 28px 20px 20px}
.wlb-slide img{width:260px;height:160px;object-fit:cover;border:1px solid var(--line)}
.wlb-st{font:600 38px var(--head);color:var(--gold);margin-bottom:8px}
.wlb-sp{font-size:28px;line-height:1.35}
.wlb-bar{position:absolute;left:0;bottom:0;height:4px;background:var(--gold);animation:wlbbar 6s linear forwards}
@keyframes wlbbar{from{width:0}to{width:100%}}

.wlb-board{position:relative;overflow:hidden;background:var(--panel);border:1px solid var(--line);display:flex;flex-direction:column;min-height:0}
.wlb-bh{font:600 40px var(--head);padding:20px 28px;border-bottom:1px solid var(--line);display:flex;justify-content:space-between;align-items:baseline}
.wlb-bh span{font:500 26px var(--body);color:var(--mute)}
.wlb-rows{display:flex;flex-direction:column;flex:1;min-height:0}
.wlb-row{flex:1;display:grid;grid-template-columns:44px 88px 1fr 240px;align-items:center;gap:14px;padding:0 24px;border-bottom:1px solid var(--line);position:relative;overflow:hidden}
.wlb-row:last-child{border:0}
.wlb-rank{font:600 52px var(--head);color:var(--mute)}
.wlb-row.first{background:linear-gradient(90deg,rgba(212,167,74,.2),transparent);animation:wlbglow 3s ease-in-out infinite}
@keyframes wlbglow{50%{background:linear-gradient(90deg,rgba(212,167,74,.34),transparent)}}
.wlb-row.first .wlb-rank{color:var(--gold)}
.wlb-row.first::after{content:"";position:absolute;top:0;bottom:0;width:140px;left:-200px;background:linear-gradient(100deg,transparent,rgba(255,255,255,.14),transparent);animation:wlbshine 5s 2s infinite}
@keyframes wlbshine{to{left:110%}}
.wlb-avw{position:relative;width:88px;height:88px}
.wlb-av{width:88px;height:88px;border-radius:50%;border:3px solid var(--line);object-fit:cover;background:var(--line);display:block}
.wlb-row.first .wlb-av{border-color:var(--gold);animation:wlbring 2s ease-out infinite}
@keyframes wlbring{0%{box-shadow:0 0 0 0 rgba(212,167,74,.7)}100%{box-shadow:0 0 0 22px rgba(212,167,74,0)}}
.wlb-crown{position:absolute;top:-22px;right:-10px;font-size:34px;transform:rotate(18deg);animation:wlbcrown 2.4s ease-in-out infinite}
@keyframes wlbcrown{50%{transform:rotate(18deg) translateY(-6px)}}
.wlb-nm{font:500 34px var(--head);letter-spacing:.02em;white-space:nowrap}
.wlb-dp{font-size:24px;color:var(--mute);margin-bottom:8px}
.wlb-meter{height:8px;background:var(--line)}
.wlb-meter i{display:block;height:100%;width:0;background:var(--gold);transition:width 1.4s cubic-bezier(.2,.8,.2,1);position:relative;overflow:hidden}
.wlb-meter i::after{content:"";position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,.55),transparent);transform:translateX(-100%);animation:wlbshimmer 2.4s infinite}
@keyframes wlbshimmer{to{transform:translateX(100%)}}
.wlb-sc{text-align:right;white-space:nowrap}
.wlb-amt{font:600 42px/1.1 var(--head)}
.wlb-ord{font-size:24px;color:var(--mute)}
.wlb-flash{position:absolute;inset:0;background:var(--gold);pointer-events:none}

.wlb-ticker{display:flex;background:#070C17;border-top:2px solid var(--gold);overflow:hidden}
.wlb-lb{flex:0 0 auto;background:var(--gold);color:var(--navy);display:flex;align-items:center;padding:0 36px;font:600 34px var(--head);letter-spacing:.04em;z-index:2}
.wlb-win{flex:1;overflow:hidden;display:flex;align-items:center}
.wlb-track{display:flex;white-space:nowrap;animation:wlbscroll 70s linear infinite}
.wlb-track span{font-size:34px;padding-right:90px}
.wlb-track span:before{content:"";display:inline-block;width:12px;height:12px;background:var(--gold);margin-right:24px;transform:rotate(45deg)}
@keyframes wlbscroll{to{transform:translateX(-50%)}}
.wlb-alert{position:absolute;left:40px;right:40px;bottom:120px;display:flex;align-items:stretch;background:var(--red);box-shadow:0 20px 60px rgba(0,0,0,.6);z-index:10}
.wlb-stage.no-ticker .wlb-alert{bottom:28px}
.wlb-ic{width:140px;background:#fff;color:var(--red);display:grid;place-items:center;font:600 90px var(--head);animation:wlbbell 1s 4}
@keyframes wlbbell{25%{transform:rotate(-14deg)}75%{transform:rotate(14deg)}}
.wlb-tx{padding:24px 36px}
.wlb-tx span{display:block;font:500 26px var(--head);letter-spacing:.08em;opacity:.85}
.wlb-tx div{font:500 52px/1.15 var(--head);margin-top:4px}

/* ---- leader spotlight (sirf Top 5 section ke andar) ---- */
.wlbs{position:absolute;inset:0;z-index:8;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:28px 30px 40px;overflow:hidden;
  background:radial-gradient(520px 440px at 50% 28%,#1D3566 0%,#121B2E 75%)}
.wlbs.champ{background:radial-gradient(520px 440px at 50% 28%,#5A4414 0%,#121B2E 75%)}
.wlbs-rankbg{position:absolute;left:50%;top:48%;transform:translate(-50%,-50%);font:600 620px/1 var(--head);color:rgba(212,167,74,.08);pointer-events:none}
.wlbs-avatar{position:relative;flex:0 0 auto;width:300px;height:300px;border-radius:50%;border:8px solid var(--gold);animation:wlbsring 2s ease-out infinite}
.wlbs-avatar img{width:100%;height:100%;border-radius:50%;object-fit:cover;display:block;background:var(--line)}
@keyframes wlbsring{0%{box-shadow:0 0 0 0 rgba(212,167,74,.6)}100%{box-shadow:0 0 0 40px rgba(212,167,74,0)}}
.wlbs-crown{position:absolute;top:-64px;right:-8px;font-size:96px;transform:rotate(18deg);animation:wlbscrown 2.4s ease-in-out infinite}
@keyframes wlbscrown{50%{transform:rotate(18deg) translateY(-10px)}}
.wlbs-info{position:relative;width:100%;min-width:0;margin-top:28px}
.wlbs-label{display:inline-block;background:var(--gold);color:var(--navy);padding:4px 22px;font:600 32px var(--head);letter-spacing:.08em;margin-bottom:14px}
.wlbs-name{font:600 76px/1.05 var(--head);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.wlbs-emp{font-size:28px;color:var(--mute);margin:4px 0 14px}
.wlbs-amt{font:600 118px/1 var(--head);color:var(--gold)}
.wlbs-ord{font-size:30px;color:var(--ivory);margin-top:8px}
.wlbs-votebar{position:absolute;left:0;right:0;bottom:0;height:14px;background:rgba(255,255,255,.1)}
.wlbs-votebar i{display:block;height:100%;background:linear-gradient(90deg,#A97E22,var(--gold),#FFE29A)}
.wlbs-c{position:absolute;bottom:-60px;font-size:40px;opacity:0;animation:wlbsrise 6s linear infinite;pointer-events:none}
@keyframes wlbsrise{0%{transform:translateY(0) rotate(0);opacity:0}12%{opacity:.95}100%{transform:translateY(-860px) rotate(50deg);opacity:0}}

@media (prefers-reduced-motion:reduce){.wlb-track,.wlb-row.first,.wlb-row.first::after,.wlb-row.first .wlb-av,.wlb-crown,.wlb-meter i::after,.wlbs-avatar,.wlbs-crown,.wlbs-c{animation:none}}
`;

/* ---------------- component ---------------- */
const CenterTV = () => {
    const navigate = useNavigate();
    const [now, setNow] = useState(new Date());
    const [slideIdx, setSlideIdx] = useState(0);
    const [alertText, setAlertText] = useState('');
    const [ready, setReady] = useState(false);
    const [scale, setScale] = useState(1);
    const [content, setContent] = useState({ videos: [], slides: [], announcements: [], alerts: [] });
    const [list, setList] = useState([]);
    const [mediaIdx, setMediaIdx] = useState(0);
    const youtubeMountRef = useRef(null);
    const activeMedia = content.videos.length ? content.videos[mediaIdx % content.videos.length] : null;
    const maxAmt = list[0]?.total_order_amt || 1;
    const loginSession = (() => {
        try {
            const tl = JSON.parse(localStorage.getItem('tldata') || 'null');
            if (tl?.login_type === 'tl') return tl;
            const center = JSON.parse(localStorage.getItem('centerdata') || 'null');
            return center?.login_type === 'center' ? center : null;
        } catch (error) {
            return null;
        }
    })();
    const loginType = loginSession?.login_type === 'tl' ? 'tl' : 'center';
    const boardCode = loginType === 'tl' ? 'TL' : 'CH';
    const boardTitle = loginType === 'tl' ? 'TL Live Board' : 'Center Head Live Board';
    const loginName = loginSession?.display_name || loginSession?.mis_name || loginSession?.center_name || 'Logout';
    const logout = () => {
        localStorage.removeItem('centerdata');
        localStorage.removeItem('tldata');
        navigate('/');
    };

    const getBoardScope = () => {
        for (const key of ['centerdata', 'tldata']) {
            try {
                const value = JSON.parse(localStorage.getItem(key) || 'null');
                const centerId = Number(value?.center_id || value?.centerid || 0);
                if (centerId > 0) {
                    return {
                        center_id: centerId,
                        tl_id: value?.login_type === 'tl' ? Number(value?.center_mis_id || 0) : 0,
                        login_type: value?.login_type === 'tl' ? 'tl' : 'center',
                    };
                }
            } catch (error) {

            }
        }
        return { center_id: 0, tl_id: 0, login_type: '' };
    };

    useEffect(() => {
        let mounted = true;
        const loadBoard = async () => {
            const res = await fetchAllDataAPI('tv-board-feed', getBoardScope());
            if (!mounted || res?.data?.status !== 1) return;
            const data = res.data.data || {};
            const nextList = sortTop(Array.isArray(data.performers) ? data.performers.map(item => ({
                ...item,
                total_order: Number(item.total_order || 0),
                total_order_amt: Number(item.total_order_amt || 0),
            })) : []);
            setContent({
                videos: Array.isArray(data.videos) ? data.videos : [],
                slides: Array.isArray(data.slides) ? data.slides : [],
                announcements: Array.isArray(data.announcements) ? data.announcements : [],
                alerts: Array.isArray(data.alerts) ? data.alerts : [],
            });
            setList(nextList);
            setReady(false);
            setTimeout(() => mounted && setReady(true), 80);
        };
        loadBoard();
        const poll = setInterval(loadBoard, 60000);
        return () => { mounted = false; clearInterval(poll); };
    }, []);

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        const r = setTimeout(() => setReady(true), 50);
        return () => { clearInterval(t); clearTimeout(r); };
    }, []);

    useEffect(() => {
        if (!content.slides.length) return undefined;
        const current = content.slides[slideIdx % content.slides.length];
        const t = setTimeout(() => setSlideIdx(i => (i + 1) % content.slides.length), Math.max(3, Number(current?.duration_seconds || 6)) * 1000);
        return () => clearTimeout(t);
    }, [content.slides, slideIdx]);

    useEffect(() => {
        if (!content.videos.length) {
            setMediaIdx(0);
            return undefined;
        }
        if (mediaIdx >= content.videos.length) setMediaIdx(0);
        return undefined;
    }, [content.videos, mediaIdx]);

    useEffect(() => {
        if (!activeMedia) return undefined;
        let disposed = false;
        let timer;
        let player;
        const next = () => !disposed && setMediaIdx(index => (index + 1) % content.videos.length);
        if (activeMedia.media_kind === 'banner') {
            timer = setTimeout(next, Math.max(3, Number(activeMedia.duration_seconds || 6)) * 1000);
        } else {
            const videoId = getYoutubeId(activeMedia.media_url || '');
            if (!videoId) timer = setTimeout(next, 1500);
            else loadYoutubeApi().then(YT => {
                if (disposed || !youtubeMountRef.current) return;
                const host = youtubeMountRef.current;
                const mount = document.createElement('div');
                host.replaceChildren(mount);
                player = new YT.Player(mount, {
                    videoId,
                    playerVars: { autoplay: 1, mute: 1, controls: 0, rel: 0, playsinline: 1 },
                    events: {
                        onReady: event => { event.target.mute(); event.target.playVideo(); },
                        onStateChange: event => { if (event.data === YT.PlayerState.ENDED) next(); },
                        onError: next,
                    },
                });
            });
        }
        return () => {
            disposed = true;
            clearTimeout(timer);
            if (player?.destroy) player.destroy();
        };
    }, [activeMedia?.id, activeMedia?.media_kind, activeMedia?.media_url, activeMedia?.duration_seconds, content.videos.length]);

    useEffect(() => {
        let hide;
        let n = 0;
        const fire = () => {
            if (!content.alerts.length) return;
            const item = content.alerts[n++ % content.alerts.length];
            setAlertText(item.content_text || item.title || '');
            clearTimeout(hide);
            hide = setTimeout(() => setAlertText(''), Math.max(15, Number(item.duration_seconds || 15)) * 1000);
        };
        const first = setTimeout(fire, 4000);
        const loop = setInterval(fire, 35000);
        const onKey = (e) => { if (e.key === 'a' || e.key === 'A') fire(); };
        window.addEventListener('keydown', onKey);
        return () => {
            clearTimeout(first); clearInterval(loop); clearTimeout(hide);
            window.removeEventListener('keydown', onKey);
        };
    }, [content.alerts]);

    useEffect(() => {
        const fit = () => setScale(Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, []);

    const stageStyle = {
        transform: `scale(${scale})`,
        left: (window.innerWidth - 1920 * scale) / 2,
        top: (window.innerHeight - 1080 * scale) / 2,
    };
    const slide = content.slides.length ? content.slides[slideIdx % content.slides.length] : null;
    const ticker = content.announcements.map((item, i) => <span key={item.id || i}>{item.content_text || item.title}</span>);
    const performerImage = value => !value ? fallbackAvatar : (/^https?:\/\//i.test(value) ? value : BASEAGENTURLS(value));

    return (
        <div className="wlb-root">
            <style>{css}</style>
            <div className={`wlb-stage ${content.announcements.length ? '' : 'no-ticker'}`} style={stageStyle}>
                <div className="wlb-top">
                    <div className="wlb-logo"><i>{boardCode}</i><button type="button" className="wlb-identity" onClick={logout} title={`Logout ${loginName}`}><strong>{boardTitle}</strong><small>{loginName}</small></button></div>
                    <div className="wlb-live"><i></i>Live</div>
                    <div className="wlb-time">
                        {now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        <span className="wlb-date">{now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                </div>

                <div className={`wlb-main ${list.length ? '' : 'no-performers'}`}>
                    <div className={`wlb-col ${content.slides.length ? '' : 'no-slides'}`}>
                        <div className="wlb-video">
                            <img src={POSTER} alt="" />
                            {activeMedia?.media_kind === 'banner'
                                ? <img className="wlb-banner" src={activeMedia.banner_url || boardMediaUrl(activeMedia.media_url)} alt={activeMedia.title || 'TV banner'} />
                                : activeMedia && <div key={`youtube-${activeMedia.id}-${mediaIdx}`} className="wlb-youtube-player" ref={youtubeMountRef} />}
                            {activeMedia && <div className="wlb-tag">{activeMedia.media_kind === 'banner' ? 'Banner' : 'Aaj ka video'}</div>}
                        </div>

                        {slide && <div className="wlb-slides">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={slideIdx}
                                    className="wlb-slide"
                                    initial={{ x: 60, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    exit={{ x: -60, opacity: 0 }}
                                    transition={{ duration: 0.6 }}
                                >
                                    <img src={slide.media_url || scene(slideIdx % 3)} alt="" />
                                    <div><div className="wlb-st">{slide.title}</div><div className="wlb-sp">{slide.content_text}</div></div>
                                </motion.div>
                            </AnimatePresence>
                            <div className="wlb-bar" key={`bar-${slideIdx}`} />
                        </div>}
                    </div>

                    {list.length > 0 && <div className="wlb-board">
                        <div className="wlb-bh">Top 5 performers <span>Aaj ka score</span></div>
                        <div className="wlb-rows">
                            {list.map((p, i) => (
                                <motion.div
                                    key={p.name}
                                    layout
                                    className={`wlb-row ${i === 0 ? 'first' : ''}`}
                                    initial={{ opacity: 0, x: 140 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                        type: 'spring', stiffness: 90, damping: 16, delay: i * 0.18 + 0.3,
                                        layout: { type: 'spring', stiffness: 80, damping: 15, delay: 0 },
                                    }}
                                >
                                    <motion.div
                                        key={`rank-${i}`}
                                        className="wlb-rank"
                                        initial={{ scale: 1.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ type: 'spring', stiffness: 200, damping: 12 }}
                                    >{i + 1}</motion.div>

                                    <div className="wlb-avw">
                                        <img className="wlb-av" src={performerImage(p.image)} alt={p.name} />
                                        {i === 0 && <span className="wlb-crown">👑</span>}
                                    </div>

                                    <div>
                                        <div className="wlb-nm">{p.name}</div>
                                        <div className="wlb-dp">{p.empId}</div>
                                        <div className="wlb-meter">
                                            <i style={{ width: ready ? `${Math.round((p.total_order_amt / maxAmt) * 100)}%` : 0 }} />
                                        </div>
                                    </div>

                                    <div className="wlb-sc">
                                        <div className="wlb-amt"><CountUp prefix="₹" value={p.total_order_amt} /></div>
                                        <div className="wlb-ord"><CountUp value={p.total_order} /> {p.total_order === 1 ? 'order' : 'orders'}</div>
                                    </div>

                                </motion.div>
                            ))}
                        </div>
                        <LeaderSpotlight list={list} imageOf={performerImage} />
                    </div>}
                </div>

                {content.announcements.length > 0 && <div className="wlb-ticker">
                    <div className="wlb-lb">Announcement</div>
                    <div className="wlb-win"><div className="wlb-track">{ticker}{ticker}</div></div>
                </div>}

                <AnimatePresence>
                    {alertText && (
                        <motion.div
                            className="wlb-alert"
                            initial={{ y: 340, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 340, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 120, damping: 16 }}
                        >
                            <div className="wlb-ic">!</div>
                            <div className="wlb-tx"><span>Zaroori suchna</span><div>{alertText}</div></div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default withAuth(CenterTV);
